import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import { AttributeCard } from '@/components/Card/AttributeCard';
import Icon from '@/components/Icon/Icon';
import type { IconName } from '@/components/Icon/iconMap';
import { Grid, Spacer, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { stacktraceNS, useTranslation } from '@/lib/i18n';

/**
 * 顧客追加の種別選択画面。
 *
 * 現状はフォーム入力 (詳細を入力) の 1 導線のみ。development-tickets の
 * add-select を雛形にしているが、顧客には CSV / 外部連携の導線が無いため
 * 「または...」セクション (AdditionalOptions) は持たない。将来 CSV 等が
 * 増えても RadioGroup 構造を流用できるよう 1 種類でもカードで提示する。
 */
export const Route = createFileRoute('/_stacktrace/customers/add-select/')({
  component: CustomerAddSelect
});
function CustomerAddSelect() {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'customers.addSelect'
  });
  useDocumentTitle(t('pageTitle'));
  return <VStack className="mx-auto w-[1000px]">
      <Spacer variant="xxl" />
      <Text variant="h2">{t('pageTitle')}</Text>
      <Spacer variant="xs" />
      <CustomerTypeRadioGroup />
    </VStack>;
}
function CustomerTypeRadioGroup() {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'customers.addSelect'
  });
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState('form');
  const requiredFields: {
    iconName: IconName;
    text: string;
  }[] = [{
    iconName: 'SquareDot',
    text: t('requiredFields.businessPartner')
  }, {
    iconName: 'SquareDot',
    text: t('requiredFields.status')
  }, {
    iconName: 'SquareDot',
    text: t('requiredFields.phase')
  }];
  const handleRadioChange = useCallback((value: string) => {
    setSelectedType(value);
  }, []);
  const handleFillOutDetails = useCallback(() => {
    navigate({
      to: '/customers/add'
    });
  }, [navigate]);
  return <>
      <Spacer variant="xl" />
      <Text variant="h4">{t('selectionTitle')}</Text>
      <Spacer variant="l" />
      <RadioGroup onValueChange={handleRadioChange} defaultValue="form">
        <Grid columns={2} rowGap={28} columnGap={24}>
          <AttributeCard title={t('formCardTitle')} titleStyle="CardHeader" onClick={() => handleRadioChange('form')} actionButton={<RadioGroupItem id="form" value="form" className="h-4 w-4" checked={selectedType === 'form'} />} actionButtonPosition="top-left" actionButtonIsInner={false} className="cursor-pointer rounded-lg bg-neutral-fill outline-solid outline-1 -outline-offset-1 outline-neutral-muted-border shadow-md">
            <VStack className="gap-3">
              <Text variant="labelSmall">{t('requiredItems')}</Text>
              {requiredFields.map(field => <Icon key={field.text} iconName={field.iconName} text={field.text} />)}
            </VStack>
          </AttributeCard>
        </Grid>
      </RadioGroup>
      <Spacer variant="xl" />
      <ActionButton icon="FilePenIcon" className="w-48" onClick={handleFillOutDetails}>
        {t('fillOutDetails')}
      </ActionButton>
    </>;
}